"use client";

import { CloseIcon } from "@dc/ui";
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from "@dc/ui/components";
import { MoreHorizIcon } from "@dc/ui/vectors";
import { useRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import type { FC } from "react";
import { CheckReadLinear, SettingsLinear } from "solar-icon-set";
import { NotificationCard } from "@/components/NotificationCard";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useMe } from "@/hooks/useMe";
import { useNotifications } from "@/hooks/useNotifications";
import { useOrgTeamPath } from "@/lib/orgTeamPath";
import { ELEMENT_TYPE } from "@/lib/tracking/analytics";

// The drawer floats over the panel surface.
const PANEL_SURFACE = { surface: "notifications_panel" };

// Hosts supply the positioning; keep this component free of it.
export const NotificationsPanel: FC = () => {
  const { notificationGroups, isLoading, markAsRead, markAllAsRead, closeDrawer } = useNotifications();
  const router = useRouter();
  const { userId } = useMe();
  const { build: buildPath } = useOrgTeamPath();
  const flags = useFeatureFlags();
  const { trackElement } = useAnalytics();
  const t = useTranslations("navigation.notifications");
  const locale = useLocale();

  // Groups are keyed TODAY, YESTERDAY or a local YYYY-MM-DD; only the panel knows the locale.
  const headingFor = (dateKey: string) => {
    if (dateKey === "TODAY") return t("today");
    if (dateKey === "YESTERDAY") return t("yesterday");
    const [year, month, day] = dateKey.split("-").map(Number);
    return new Intl.DateTimeFormat(locale, { month: "long", day: "numeric" }).format(new Date(year, month - 1, day));
  };

  const openNotificationSettings = () => {
    if (!userId) return;
    closeDrawer();
    router.push(buildPath(`/profile/${userId}/notifications`));
  };

  return (
    <>
      <div className="z-10 flex flex-initial items-center justify-between px-5 py-3 shadow-sm">
        <h2 className="font-normal text-2xl text-foreground leading-6">{t("title")}</h2>
        {flags["werb-notification-settings"] ? (
          <div className="flex items-center gap-2">
            <Dropdown placement="bottom-end">
              <DropdownTrigger>
                <Button
                  size="sm"
                  variant="light"
                  isIconOnly
                  aria-label={t("options")}
                  {...trackElement(ELEMENT_TYPE.Button, "Notifications Actions Menu Dropdown", PANEL_SURFACE)}
                >
                  <MoreHorizIcon className="size-5" />
                </Button>
              </DropdownTrigger>
              <DropdownMenu aria-label={t("options")}>
                <DropdownItem
                  key="mark-all-read"
                  startContent={<CheckReadLinear />}
                  onPress={markAllAsRead}
                  {...trackElement(ELEMENT_TYPE.DropdownItem, "Mark All Notifications As Read", PANEL_SURFACE)}
                >
                  {t("mark_all_as_read")}
                </DropdownItem>
                <DropdownItem
                  key="settings"
                  startContent={<SettingsLinear />}
                  onPress={openNotificationSettings}
                  {...trackElement(ELEMENT_TYPE.DropdownItem, "Email Notification Settings", PANEL_SURFACE)}
                >
                  {t("email_settings")}
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
            <Button size="sm" variant="light" isIconOnly onPress={closeDrawer} aria-label={t("close")}>
              <CloseIcon fontSize={16} />
            </Button>
          </div>
        ) : (
          <>
            <Button size="sm" variant="bordered" onPress={markAllAsRead}>
              {t("mark_all_as_read")}
            </Button>
            <Button size="sm" variant="light" isIconOnly onPress={closeDrawer} aria-label={t("close")}>
              <CloseIcon fontSize={16} />
            </Button>
          </>
        )}
      </div>
      <div className="flex min-h-0 w-full flex-1 flex-col overflow-y-auto bg-foreground-50 px-0 py-2">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <p className="text-content3-foreground">{t("loading")}</p>
          </div>
        ) : notificationGroups.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <p className="text-content3-foreground">{t("empty")}</p>
          </div>
        ) : (
          <div className="flex w-full flex-col gap-3">
            {notificationGroups.map((group) => (
              <div key={group.date} className="flex flex-col">
                <div className="px-2.5 py-2">
                  <p className="font-mono font-semibold text-secondary text-tiny uppercase leading-4 tracking-wide">
                    {headingFor(group.date)}
                  </p>
                </div>
                <div className="mt-2 flex flex-col gap-3 px-2.5">
                  {group.notifications.map((notification) => (
                    <NotificationCard
                      key={notification.id}
                      {...notification}
                      onMarkAsRead={markAsRead}
                      onNavigate={closeDrawer}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};
